import Map from 'ol/Map';
import View from 'ol/View';
import { fromLonLat } from 'ol/proj';
import { Tile, Vector as VectorLayer } from 'ol/layer';
import { XYZ, Vector as VectorSource } from 'ol/source';
import { defaults as defaultControls } from 'ol/control';
import { GeoJSON } from 'ol/format';
import { Style, Stroke, Fill } from 'ol/style';
import { jsonpRequest } from '../utils/jsonp';

const API_KEY = process.env.REACT_APP_VWORLD_API_KEY || '';

export interface LocationResult {
  success: boolean;
  error?: string;
}

export type LocationCallback = (result: LocationResult) => void;

class MapManager {
  private map: Map | null = null;

  private cadastralLayer: VectorLayer<VectorSource> | null = null;

  /**
   * VWorld Data API를 JSONP 방식으로 호출하는 유틸리티 메서드 (재시도 로직 포함)
   */
  private static async loadDataAPIWithJSONP(params: Record<string, string>, maxRetries = 3): Promise<any> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await jsonpRequest('https://api.vworld.kr/req/data', params);

        // VWorld Data API 응답 구조 확인
        if (response.response && response.response.result && response.response.result.featureCollection) {
          return response.response.result.featureCollection;
        }

        // 다른 가능한 응답 구조들 확인
        if (response.featureCollection) {
          return response.featureCollection;
        }

        if (response.features) {
          return { type: 'FeatureCollection', features: response.features };
        }

        // 오류 응답 확인
        if (response.response?.status === 'ERROR') {
          console.warn(`⚠️ Data API 오류 (시도 ${attempt}/${maxRetries}):`, response.response.status);
          if (attempt < maxRetries) {
            // 재시도 전 잠시 대기 (백오프 전략)
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => {
              setTimeout(resolve, 1000 * attempt);
            });
          } else {
            return response;
          }
        } else {
          return response;
        }
      } catch (error) {
        console.warn(`⚠️ Data API JSONP 요청 실패 (시도 ${attempt}/${maxRetries}):`, error);
        lastError = error as Error;

        if (attempt < maxRetries) {
          // 재시도 전 잠시 대기 (백오프 전략)
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 1000 * attempt);
          });
        }
      }
    }

    // 모든 재시도 실패 시 마지막 오류 던지기
    console.error('❌ Data API JSONP 요청 최종 실패:', lastError);
    throw lastError || new Error('All retry attempts failed');
  }

  /**
   * 지도가 초기화되어 준비되었는지 확인합니다.
   */
  isMapReady(): boolean {
    return this.map !== null;
  }

  initialize(target: string): Map {
    // VWorld 위성 이미지 + 라벨 조합 레이어
    const satelliteWithLabelsLayer = new XYZ({
      // VWorld 위성 이미지와 라벨이 함께 포함된 레이어
      url: `https://api.vworld.kr/req/wmts/1.0.0/${API_KEY}/Satellite/{z}/{y}/{x}.jpeg`,
      crossOrigin: 'anonymous',
      transition: 0,
    });

    // 추가 라벨 레이어 (더 상세한 라벨 정보)
    const additionalLabelLayer = new XYZ({
      url: `https://api.vworld.kr/req/wmts/1.0.0/${API_KEY}/Hybrid/{z}/{y}/{x}.png`,
      crossOrigin: 'anonymous',
      transition: 0,
    });

    this.map = new Map({
      target,
      layers: [
        new Tile({
          source: satelliteWithLabelsLayer,
          preload: 4,
        }),
        new Tile({
          source: additionalLabelLayer,
          preload: 4,
        }),
      ],
      view: new View({
        center: fromLonLat([127.55, 35.871]),
        zoom: 7.5,
        minZoom: 6, // VWorld 타일 서비스 지원 범위로 조정
        maxZoom: 23,
      }),
      controls: defaultControls({ zoom: false }),
    });

    return this.map;
  }

  /**
   * 지적도 중심을 기준으로, 지정된 크기와 여백을 가진 영역을 잘라낸 이미지 데이터를 반환합니다.
   * @param frameWidth - 결과물 이미지의 너비 (CSS 픽셀 단위)
   * @param frameHeight - 결과물 이미지의 높이 (CSS 픽셀 단위)
   * @param zoomOutFactor - 캡처 영역의 줌 아웃 계수. 1보다 크면 더 넓은 영역을 캡처합니다.
   * @param yOffsetInPixels - 캡처 영역의 Y축 오프셋. 양수 값은 아래로 이동.
   * @returns {Promise<string | null>} Base64로 인코딩된 잘라낸 이미지 데이터 URL
   */
  public async captureFramedArea(
    frameWidth: number,
    frameHeight: number,
    zoomOutFactor = 1,
    yOffsetInPixels = 0,
  ): Promise<string | null> {
    if (!this.map) {
      console.error('Map is not initialized.');
      return null;
    }
    if (!this.cadastralLayer || !this.cadastralLayer.getSource()?.getFeatures().length) {
      console.error('Cadastral layer with features not found for centering.');
      return null;
    }

    const mapContainer = this.map.getTargetElement();
    if (!mapContainer || typeof mapContainer === 'string') {
      console.error('지도 컨테이너 요소를 찾을 수 없습니다.');
      return null;
    }
    const mapCanvas = mapContainer.querySelector('canvas');
    if (!mapCanvas) {
      console.error('지도 컨테이너 내부에서 canvas 요소를 찾을 수 없습니다.');
      return null;
    }

    let fullMapDataUrl: string;
    try {
      fullMapDataUrl = mapCanvas.toDataURL('image/png');
    } catch (error) {
      console.error('지도를 이미지로 변환하는 데 실패했습니다. CORS 정책이나 canvas 보안 설정을 확인하세요.', error);
      return null;
    }

    return new Promise((resolve) => {
      const fullMapImage = new Image();
      fullMapImage.crossOrigin = 'anonymous';

      fullMapImage.onload = () => {
        const cadastralExtent = this.cadastralLayer!.getSource()!.getExtent();
        const cadastralCenterCoordinate = [
          (cadastralExtent[0] + cadastralExtent[2]) / 2,
          (cadastralExtent[1] + cadastralExtent[3]) / 2,
        ];
        const featureCenterPixel = this.map!.getPixelFromCoordinate(cadastralCenterCoordinate);

        if (!featureCenterPixel) {
          console.error('Could not get pixel for feature center.');
          resolve(null);
          return;
        }

        const sourceWidth = frameWidth * zoomOutFactor;
        const sourceHeight = frameHeight * zoomOutFactor;

        const sx = featureCenterPixel[0] - sourceWidth / 2;
        const sy = featureCenterPixel[1] - sourceHeight / 2 + yOffsetInPixels;

        // 고해상도 디스플레이(레티나 등)에서 선명한 이미지를 위해
        // 브라우저의 픽셀 비율(devicePixelRatio)을 모든 픽셀 계산에 곱해줍니다.
        const pixelRatio = window.devicePixelRatio || 1;

        const sx_scaled = sx * pixelRatio;
        const sy_scaled = sy * pixelRatio;
        const sWidth_scaled = sourceWidth * pixelRatio;
        const sHeight_scaled = sourceHeight * pixelRatio;

        // 최종 결과물 캔버스를 생성합니다.
        // 실제 저장되는 이미지의 픽셀 크기는 (frameWidth * pixelRatio) x (frameHeight * pixelRatio)가 됩니다.
        // 예를 들어 pixelRatio가 2이면 620x350 크기의 이미지가 생성되지만, 화면 비율은 310x175와 동일합니다.
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = frameWidth * pixelRatio;
        cropCanvas.height = frameHeight * pixelRatio;

        const ctx = cropCanvas.getContext('2d')!;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          fullMapImage,
          sx_scaled,
          sy_scaled,
          sWidth_scaled,
          sHeight_scaled,
          0,
          0,
          cropCanvas.width,
          cropCanvas.height,
        );

        resolve(cropCanvas.toDataURL('image/png'));
      };

      fullMapImage.onerror = () => {
        console.error('Failed to load map image for cropping.');
        resolve(null);
      };

      fullMapImage.src = fullMapDataUrl;
    });
  }

  /**
   * 지도를 특정 위치로 이동시킵니다.
   */
  private moveToLocation(longitude: number, latitude: number, zoom: number = 6): void {
    const location = fromLonLat([longitude, latitude]);
    this.map!.getView().setCenter(location);
    this.map!.getView().setZoom(zoom);
  }

  /**
   * 지적도 영역에 여백을 많이 주어 적당한 거리에서 보여줍니다.
   */
  private fitToExtentWithDistance(extent: any): void {
    this.map!.getView().fit(extent, {
      padding: [100, 150, 500, 150], // 큰 여백으로 적당한 거리 유지
      maxZoom: 18, // 낮은 최대 줌으로 너무 가까이 가지 않음
      duration: 200,
    });
  }

  /**
   * 좌표를 사용해서 OpenLayers WFS로 지적도를 직접 로드하여 표시합니다.
   */
  async loadAndDisplayCadastralLayer(pnu: string, longitude?: number, latitude?: number): Promise<LocationResult> {
    if (!this.map) {
      return { success: false, error: '지도가 초기화되지 않았습니다.' };
    }

    try {
      // 좌표가 없으면 PNU만으로는 조회 불가
      if (!longitude || !latitude) {
        return { success: false, error: '지적도 조회를 위한 좌표 정보가 필요합니다.' };
      }

      // PNU 코드를 활용한 정확한 필지 검색
      // VWorld Data API에서 PNU는 'pnu' 속성으로 저장됨
      const dataParams = {
        key: API_KEY,
        service: 'data',
        version: '2.0',
        request: 'getfeature',
        format: 'json',
        size: '100',
        page: '1',
        data: 'LP_PA_CBND_BUBUN',
        geometry: 'true',
        attribute: 'true',
        crs: 'EPSG:4326',
        attrFilter: `pnu:=:${pnu}`, // PNU 코드로 정확한 필지 검색
      };

      // JSONP로 Data API 호출
      let dataResponse = await MapManager.loadDataAPIWithJSONP(dataParams);

      if (!dataResponse || !dataResponse.features || dataResponse.features.length === 0) {
        console.warn('⚠️ [DEBUG] PNU 기반 검색 실패, 좌표 기반 검색으로 폴백');

        // PNU 기반 검색 실패 시 좌표 기반 검색으로 폴백
        const fallbackParams = {
          key: API_KEY,
          service: 'data',
          version: '2.0',
          request: 'getfeature',
          format: 'json',
          size: '100',
          page: '1',
          data: 'LP_PA_CBND_BUBUN',
          geometry: 'true',
          attribute: 'true',
          crs: 'EPSG:4326',
          geomFilter: `POINT(${longitude} ${latitude})`,
        };

        const fallbackResponse = await MapManager.loadDataAPIWithJSONP(fallbackParams);

        if (!fallbackResponse || !fallbackResponse.features || fallbackResponse.features.length === 0) {
          // 기존 지적도 레이어가 있으면 제거
          if (this.cadastralLayer) {
            this.map.removeLayer(this.cadastralLayer);
            this.cadastralLayer = null;
          }
          // 지적도 데이터가 없어도 해당 위치로 이동
          this.moveToLocation(longitude!, latitude!);
          return { success: false, error: '해당 PNU의 지적도 데이터를 찾을 수 없습니다.' };
        }

        // 폴백 응답 사용
        dataResponse = fallbackResponse;
      }

      // 선택한 PNU와 일치하는 Feature 찾기
      let targetFeature = null;
      if (dataResponse.features.length > 1) {
        // 여러 Feature가 있을 경우 PNU가 정확히 일치하는 것 찾기
        targetFeature = dataResponse.features.find((feature: any) => {
          return feature.properties?.pnu === pnu || feature.id === pnu;
        });

        if (targetFeature) {
          // 일치하는 Feature만 사용
          dataResponse = {
            ...dataResponse,
            features: [targetFeature],
          };
        }
      }

      // GeoJSON 형식으로 변환 (VWorld는 EPSG:4326 좌표계 사용)
      const geojsonFormat = new GeoJSON({
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });

      // 벡터 소스 생성 (JSONP로 받은 데이터 사용)
      const vectorSource = new VectorSource({
        features: geojsonFormat.readFeatures(dataResponse),
      });

      // 지적도 스타일 정의
      const cadastralStyle = new Style({
        stroke: new Stroke({
          color: '#445EFF',
          width: 1,
        }),
        fill: new Fill({
          color: 'rgba(68, 94, 255, 0.6)',
        }),
      });

      // 피처 수 확인
      const features = vectorSource.getFeatures();

      if (features.length > 0) {
        // 지적도 영역 계산
        const extent = vectorSource.getExtent();

        // 기존 레이어가 있으면 소스만 교체, 없으면 새로 생성
        if (this.cadastralLayer) {
          // 기존 레이어의 소스를 새로운 소스로 교체 (깜빡임 최소화)
          this.cadastralLayer.setSource(vectorSource);
          this.cadastralLayer.setStyle(cadastralStyle);
        } else {
          // 최초 생성 시에만 새 레이어 생성
          this.cadastralLayer = new VectorLayer({
            source: vectorSource,
            style: cadastralStyle,
          });
          this.map.addLayer(this.cadastralLayer);
        }

        // 지도 이동 (소스 교체 후) - 적당한 거리에서 보여주기
        this.fitToExtentWithDistance(extent); // 줌 레벨을 15로 낮춰서 더 멀리 보기

        return { success: true };
      }

      // 지적도 데이터가 없어도 해당 위치로 이동
      this.moveToLocation(longitude!, latitude!);
      return { success: false, error: '해당 PNU의 지적도 데이터를 찾을 수 없습니다.' };
    } catch (error) {
      // 기존 지적도 레이어가 있으면 제거
      if (this.cadastralLayer) {
        this.map.removeLayer(this.cadastralLayer);
        this.cadastralLayer = null;
      }
      // 오류 발생 시에도 해당 위치로 이동
      this.moveToLocation(longitude!, latitude!);
      const errorMessage = error instanceof Error ? error.message : '지적도 표시 중 알 수 없는 오류가 발생했습니다.';
      return { success: false, error: errorMessage };
    }
  }

  /**
   * 지적도 레이어를 제거합니다.
   */
  removeCadastralLayer(): void {
    if (this.map && this.cadastralLayer) {
      this.map.removeLayer(this.cadastralLayer);
      this.cadastralLayer = null;
    }
  }

  cleanup(): void {
    if (this.map) {
      this.map.setTarget(undefined);
      this.map = null;
      this.cadastralLayer = null;
    }
  }
}

export default new MapManager();
